import { WESOG_SESSION_STORE_PARAMS, CAPTCHA_STORE_PARAMS } from '@core/constants/STORE';
import { WESOG_CENTER_STORE } from '@core/constants/CONSTANTS';
import { createUserObj } from '@core/utils/wesog-user';

/**
 * Create the store in local Storage.
 * @returns {void}
 */
export const createSessionStore = () => {
    sessionStorage.setItem(
        WESOG_CENTER_STORE,
        JSON.stringify({
            [WESOG_SESSION_STORE_PARAMS.USER]: createUserObj(),
            [WESOG_SESSION_STORE_PARAMS.CURRENT_SELECTED_IMAGE]: {},
            [WESOG_SESSION_STORE_PARAMS.CURRENT_QUERY]: {},
            [WESOG_SESSION_STORE_PARAMS.LAST_REPORT_SELECTED]: {},
            [WESOG_SESSION_STORE_PARAMS.REDIRECT_HOME_REPORT_VIEW]: 'false',
            [WESOG_SESSION_STORE_PARAMS.CAPTCHA]: {
                [CAPTCHA_STORE_PARAMS.EXPIRED_IN]: undefined,
                [CAPTCHA_STORE_PARAMS.FIRST_LOAD]: 'true',
            },
        }),
    );
};

/**
 * Get the store from session Storage.
 * @returns {{
 *  user: Object,
 * }} The store.
 */
export const getSessionStore = () => {
    let /** @type {Object} */ store = JSON.parse(
            sessionStorage.getItem(WESOG_CENTER_STORE),
        );

    if (!store) {
        createSessionStore();

        store = JSON.parse(sessionStorage.getItem(WESOG_CENTER_STORE));
    }

    return store;
};

/**
 * Get a value from the store.
 * @param {String} key - The key.
 * @returns {any} - The value.
 */
export const getSessionStoreValue = (key) => {
    const /** @type {Object} */ store = getSessionStore();

    return store ? store[key] : undefined;
};

/**
 * Update the store in session Storage.
 * @param {Object} store - The store.
 * @returns {void}
 */
export const updateSessionStore = (store) => {
    sessionStorage.setItem(WESOG_CENTER_STORE, JSON.stringify(store));
};

/**
 * Update a value in the store.
 * @param {String} key
 * @param {any} value
 * @returns {void}
 */
export const updateSessionStoreValue = (key, value) => {
    const /** @type {Object} */ store = getSessionStore();
    store[key] = value;

    updateSessionStore(store);
};

export const removeSessionStore = () => {
    sessionStorage.removeItem(WESOG_CENTER_STORE);
};

export const removeSessionStoreValue = (key) => {
    const /** @type {Object} */ store = getSessionStore();
    store[key] = '';

    updateSessionStore(store);
};

/**
 * Check if the store has a value.
 * @param {String} key - The key.
 * @returns {boolean} - True if the store has the value.
 */
export const hasStoreValue = (key) => {
    return getSessionStore(key) !== undefined;
};

/**
 * Get the user from the store.
 * @returns {{
 *  username: string,
 *  access_token: string,
 *  expires_in: number,
 *  searcherHistory: Array<object>,
 *  lastName: string,
 *  email: string,
 *  id: string,
 *  language: string,
 *  phone: string,
 *  pseudo: string,
 *  lightbox: Array<object>,
 * }|undefined} - The user.
 */
export const getSessionStoreUser = () => {
    const /** @type {Object} */ store = getSessionStore();

    return store ? store[WESOG_SESSION_STORE_PARAMS.USER] : undefined;
};

/**
 * Update the user in the store.
 * @param {Object} user - The user.
 * @returns {void}
 */
export const updateSessionStoreUser = (user) => {
    const /** @type {Object} */ store = getSessionStore();
    store[WESOG_SESSION_STORE_PARAMS.USER] = user;

    updateSessionStore(store);
};

/**
 * Remove the user from the store.
 * @returns {void}
 */
export const removeSessionStoreUser = () => {
    const /** @type {Object} */ store = getSessionStore();
    delete store[WESOG_SESSION_STORE_PARAMS.USER];

    updateSessionStore(store);
};

/**
 * Update the timer state in the session storage.
 * @param {number} time - The time in milliseconds.
 * @returns {void}
 */
export const updateTimerState = (time) => {
    const /** @type {Object} */ store = getSessionStore();
    store[WESOG_SESSION_STORE_PARAMS.CAPTCHA] = {
        ...store[WESOG_SESSION_STORE_PARAMS.CAPTCHA],
        [CAPTCHA_STORE_PARAMS.EXPIRED_IN]: time,
    };

    updateSessionStore(store);
};

/**
 * Get the timer state from the session storage.
 * @returns {number} - The time in milliseconds.
 */
export const getTimerState = () => {
    const /** @type {Object} */ store = getSessionStore();

    return (
        store[WESOG_SESSION_STORE_PARAMS.CAPTCHA][CAPTCHA_STORE_PARAMS.EXPIRED_IN] || 0
    );
};
